import { useCallback, useEffect } from 'react'

import { hashKey, useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { cleanFileName } from '@/helpers/file'
import { httpRangeSource } from '@/helpers/rangeSource'

import { fileQueryKeys } from '@/hooks/files/types'
import { fetchFile } from '@/hooks/files/useFile'

import { type OpenZip, openZip } from '@/lib/ooxml/rangeArchive'

import type { File as ServerFile } from '@/types/File'

export type PackageSlimmer = (zip: OpenZip, name: string) => Promise<File>

/**
 * A fresh presigned URL for when the record's has expired. The same function
 * until the file or the reader changes, so a memoized viewer handed it stays put.
 */
export const useRefreshUrl = (fileId: string) => {
  const auth = useAuthInfo()
  const token = auth?.accessToken
  return useCallback(
    async () => (await fetchFile(fileId, token, true)).url,
    [fileId, token],
  )
}

/** A number per slimmer, for query keys: each is a module-level function. */
const slimmerIds = new WeakMap<PackageSlimmer, number>()
let lastSlimmerId = 0
const slimmerId = (slim: PackageSlimmer) => {
  if (!slimmerIds.has(slim)) slimmerIds.set(slim, (lastSlimmerId += 1))
  return slimmerIds.get(slim)!
}

interface UsePartialPackageArgs {
  file: ServerFile
  slim: PackageSlimmer
  enabled?: boolean
  /**
   * For a reader with the whole file coming another way: from a server that
   * ignores the range it fails rather than download a second copy.
   */
  rangesOnly?: boolean
}

/**
 * An Office file cut down to what its first page needs, read by byte range
 * from the file's presigned URL: the rest of the file is never downloaded.
 * `failed` once the file would not open; the card keeps its icon.
 *
 * Cached like any other read of the file, so a card the chat scrolls away and
 * back, or a second card showing the same file, reads nothing again. A read
 * nobody is waiting on any more — its cards unmounted, or turned off — is
 * aborted, and its bytes let go.
 */
export const usePartialPackage = ({
  file,
  slim,
  enabled = true,
  rangesOnly = false,
}: UsePartialPackageArgs): { slimFile: File | null; failed: boolean } => {
  const refreshUrl = useRefreshUrl(file.id)
  const queryClient = useQueryClient()
  const queryKey = fileQueryKeys.partialPackage({
    fileId: file.id,
    revision: `${file.last_modified}:${file.size}`,
    slimmer: slimmerId(slim),
    rangesOnly,
  })

  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      try {
        const size =
          file.size ??
          Number(
            (await fetch(file.url, { method: 'HEAD', signal })).headers.get(
              'Content-Length',
            ),
          )
        const source = httpRangeSource({
          url: file.url,
          size,
          signal,
          refreshUrl,
          rangesOnly,
        })
        return await slim(await openZip(source), cleanFileName(file.name))
      } catch (error) {
        if (!signal.aborted) {
          console.warn('[partial file] could not read file', {
            fileId: file.id,
            error: error instanceof Error ? error.message : String(error),
          })
        }
        throw error
      }
    },
    enabled,
    staleTime: Infinity,
    // A file that would not open once will not open on a second try; the
    // card shows its icon rather than wait.
    retry: false,
  })

  // Turned off while still reading: nobody wants the bytes any more. Unmounted
  // cards need nothing here; the last one to go aborts the read.
  const readKey = hashKey(queryKey)
  useEffect(() => {
    if (!enabled) void queryClient.cancelQueries({ queryKey, exact: true })
    // The key's hash says everything the key does.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, readKey, queryClient])

  return enabled
    ? { slimFile: query.data ?? null, failed: query.isError }
    : { slimFile: null, failed: false }
}
